import { useCallback, useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import type { OrganizationTaskTemplateRow } from "./organizationTaskTemplatesViewConfig";

export function useOrganizationTaskTemplateRowSelection(
    rows: OrganizationTaskTemplateRow[],
) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const visibleIds = useMemo(
        () => new Set(rows.map((row) => row.id)),
        [rows],
    );

    useEffect(() => {
        setRowSelection((selection) => {
            const pruned = Object.fromEntries(
                Object.entries(selection).filter(
                    ([id, isSelected]) => isSelected && visibleIds.has(id),
                ),
            );
            return Object.keys(pruned).length === Object.keys(selection).length
                ? selection
                : pruned;
        });
    }, [visibleIds]);

    const selectedIds = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([id, isSelected]) => isSelected && visibleIds.has(id))
                .map(([id]) => id),
        [rowSelection, visibleIds],
    );

    const clearSelection = useCallback(() => setRowSelection({}), []);

    return { rowSelection, setRowSelection, selectedIds, clearSelection };
}
